// Copyright 2019 Sorama B.V.

import { autoinject, computedFrom, Disposable } from "aurelia-framework";
import { HttpResponseMessage, RequestMessage } from "aurelia-http-client";
import { OpenIdConnect } from "aurelia-open-id-connect";
import { RouterConfiguration } from "aurelia-router";
import { parseUri } from "extensions/UriExtensions";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler, IWebRequestInterceptorHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { User as OidcUser } from "oidc-client";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { OpenIDUnauthorizedNavigationStrategy } from "./OpenIDUnauthorizedNavigationStrategy";

@autoinject()
export class OpenIDAuthentication implements IAuthentication, IWebRequestInterceptorHandler {
    private authenticationHandlers: IAuthenticationHandler[];
    private oldServerStatus: ServerStatus = ServerStatus.Online;

    private ensureSignInBusy = false;
    private _signedIn = false;
    private _userName: string;
    private _userId: string;
    private _businessUnitId: string;

    //#region Public readonly user properties

    @computedFrom("_signedIn")
    public get isSignedIn(): boolean {
        return this._signedIn;
    }

    @computedFrom("_userName")
    public get userName(): string {
        return this._userName;
    }

    @computedFrom("_userId")
    public get userId(): string {
        return this._userId;
    }

    @computedFrom("_businessUnitId")
    public get businessUnitId(): string {
        return this._businessUnitId;
    }

    //#endregion

    public isBusy: boolean;

    constructor(
        private readonly webRequestHandler: IWebRequestHandler,
        private readonly serverStatusService: IServerStatusService,
        private readonly oidConnect: OpenIdConnect,
        private readonly unauthorizedStrategy: OpenIDUnauthorizedNavigationStrategy,
        private readonly notificationService: INotificationService
    ) {
        // Flag busy until configure is completed
        this.isBusy = true;

        this.authenticationHandlers = [];
        this.oidConnect.userManager.events.addUserLoaded(() => this.ensureSignInState());
        this.oidConnect.userManager.events.addUserSignedOut(() => this.ensureSignInState());
        this.oidConnect.userManager.events.addUserUnloaded(() => this.ensureSignInState());

        if (this.serverStatusService.addServerStatusHandler) {
            this.serverStatusService.addServerStatusHandler(this.onlineChanged);
        }

        const baseUri = parseUri(Api.baseUri);
        const computeUri = parseUri(Api.computeUri);
        const identityUri = parseUri(Api.identityUri);

        // Add interceptor for the sorama domain
        if (this.webRequestHandler.addInterceptor) {
            this.webRequestHandler.addInterceptor(baseUri.host, this);
            this.webRequestHandler.addInterceptor(computeUri.host, this);
            this.webRequestHandler.addInterceptor(identityUri.host, this);
        }
    }

    public configure(router: RouterConfiguration): Promise<void> {
        // Configure the router
        this.oidConnect.configure(router);

        return this.ensureSignInState();
    }

    //#region IWebRequestInterceptorHandler Members
    public async onSendRequest(request: RequestMessage): Promise<void> {
        const user = await this.getUser();
        if (user) {
            request.headers.add("AUTHORIZATION", "Bearer " + user.access_token);
        }
    }

    public onReceiveResponse(): Promise<void> {
        return Promise.resolve();
    }

    public onResponseError(error: HttpResponseMessage): Promise<boolean> {
        if (error.statusCode === 401 && this.isSignedIn) {
            return this.oidConnect
                .loginSilent()
                .then(() => true) // We're logged in again :)
                .catch(async (e) => {
                    // We cannot login automatically again, so notify the user that the session is expired.

                    // See: https://openid.net/specs/openid-connect-core-1_0.html
                    if (e.error && e.error != "interaction_required" && e.error != "consent_required") {
                        try {
                            // There is something wrong with the authentication itself.
                            // Since the proper "log out flow" is not triggered, remove all users. It is not required to revoke any token, since it is not valid. Hence the error ;)
                            await this.oidConnect.userManager.removeUser();
                        } catch {
                            // if it fails, just continue. It should not happen, but you never know..
                            console.warn("OpenIDAUth: unable to remove users from cache");
                        }
                    }

                    this.onSignOut();
                    this.notifyHandlers();

                    this.notificationService.registerNotification(
                        "Authentication",
                        "You have been logged out for safety reasons, please login again",
                        NotificationUrgency.Warning
                    );

                    return false;
                });
        }

        return Promise.resolve(false);
    }
    //#endregion

    //#region IAuthentication Members
    public signIn(): Promise<void> {
        this.isBusy = true;
        // Return a promise that does not resolve unless it fails
        return new Promise((resolve, reject) => {
            const args = this.unauthorizedStrategy.getRedirectArgs();
            this.oidConnect.login(args).catch((e) => reject(e));
        });
    }

    public signOut(): Promise<void> {
        this.isBusy = true;

        // Return a promise that does not resolve unless it fails
        return new Promise((resolve, reject) => {
            return this.oidConnect.logout().catch((e) => reject(e));
        });
    }

    public getUser(): Promise<OidcUser> {
        return this.oidConnect.getUser();
    }

    public addAuthenticationHandler(handler: IAuthenticationHandler): Disposable {
        this.authenticationHandlers.push(handler);

        if (this.isSignedIn) {
            handler.onSignIn(this.userId);
        }

        return {
            dispose: () => {
                const index = this.authenticationHandlers.indexOf(handler);
                if (index === -1) return;

                this.authenticationHandlers.splice(index, 1);
            },
        };
    }

    //#endregion

    //#region Sign in handlers

    private ensureSignInState(): Promise<void> {
        if (this.ensureSignInBusy) return Promise.resolve();

        this.isBusy = true;
        this.ensureSignInBusy = true;

        return this.oidConnect
            .getUser()
            .then((user) => {
                if (!user) {
                    return this.userChanged(undefined);
                }

                if (user.expires_in > 5 * 60) {
                    // User should be fine
                    return this.userChanged(user);
                }

                // User is close to expire (or has expired), do a silent signin to make sure our state is ok
                return this.oidConnect.loginSilent().then((newUser) => {
                    return this.userChanged(newUser);
                });
            })
            .catch(() => {
                return this.oidConnect.userManager
                    .removeUser()
                    .catch(() => {})
                    .then(() => {
                        return this.userChanged(undefined);
                    });
            })
            .then(() => {
                this.isBusy = false;
                this.ensureSignInBusy = false;
            });
    }

    private userChanged = async (user: OidcUser): Promise<void> => {
        // Firstly check if the user has actually changed
        if (!user && !this._signedIn) return; // No change

        if (user && this._signedIn && user.profile.sub === this._userId) return; // No change

        this.isBusy = true;

        if (!user) {
            this.onSignOut();
            return;
        }

        this.onAuthenticationSuccess(user);
        await this.notifyHandlers();
        this.isBusy = false;
    };

    private onlineChanged = (newStatus: ServerStatus): Promise<void> => {
        if (newStatus === ServerStatus.Online && this.oldServerStatus !== ServerStatus.Online) {
            this.oldServerStatus = ServerStatus.Online;
            return this.ensureSignInState();
        }
    };

    private onSignOut(): void {
        this._signedIn = false;
        this._userName = undefined;
        this._userId = undefined;
        this._businessUnitId = undefined;
    }

    //#endregion

    //#region Helpers

    private onAuthenticationSuccess(user: OidcUser): void {
        // Set fields
        this._businessUnitId = user.profile.businessUnitId;
        this._userName = user.profile.preferred_username;
        this._userId = user.profile.sub;
        this._signedIn = true;
    }

    private notifyHandlers(): Promise<void> {
        const promises = <Promise<unknown>[]>[];

        for (let i = 0; i < this.authenticationHandlers.length; i++) {
            const handler = this.authenticationHandlers[i];
            let promise = this.isSignedIn ? handler.onSignIn(this.userId) : handler.onSignOut();

            // catch all unintended side effect because of subscribers are not doing their shit right
            promise = promise.catch(() => {
                return;
            });

            promises.push(promise);
        }
        return Promise.all(promises)
            .then(() => {})
            .catch(() => {});
    }

    //#endregion
}
